import * as THREE from "three";
import {
  acquireMesh as acquireMeshFromCache,
  acquireTexture as acquireTextureFromCache,
} from "../../runtime/assetCache";
import { getAssetById } from "../../stores/assetStore";
import { getSceneState } from "../../stores/sceneStore";
import { createDefaultPhongMaterial } from "../../utils/materialDefaults";
import { logger } from "../../utils/logger";
import { getPreviewTransformForMeshUuid } from "../../mesh-editor/previewState";
import { getVertexTransformFromMjcfMeshRef } from "../mjcfMeshReferenceTransform";
import {
  applyColorMapToTexture,
  applyMaterialTransparencySettings,
  applyTextureRoleSettings,
  buildMaterial,
  createCollisionMaterial,
} from "../materialFactory";

const devLog = () => {};
const MESH_CACHE_FAILURE_TOAST_MS = 60000;
const meshCacheFailureToastTimes = new Map();
const missingMeshAssetReports = new Map();
let missingMeshAssetFlushQueued = false;

function dispatchThrottledMeshCacheFailureToast(assetId) {
  if (typeof window === "undefined") return;
  const key = assetId || "__unknown_mesh_asset__";
  const now = Date.now();
  const last = meshCacheFailureToastTimes.get(key) || 0;
  if (now - last < MESH_CACHE_FAILURE_TOAST_MS) return;
  meshCacheFailureToastTimes.set(key, now);
  try {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: {
          message: "A mesh failed to load. Try reloading the page or re-uploading the asset.",
        },
      })
    );
  } catch {
    // Toasts are best-effort UI feedback.
  }
}

function flushMissingMeshAssetReports() {
  missingMeshAssetFlushQueued = false;
  const reports = Array.from(missingMeshAssetReports.values());
  missingMeshAssetReports.clear();
  if (reports.length === 0) return;
  logger.warn("[processBodyFromStore] Missing mesh assets", {
    count: reports.length,
    assets: reports.map((report) => ({
      assetId: report.assetId,
      geomCount: report.geomCount,
      meshIds: Array.from(report.meshIds).slice(0, 8),
      bodyNames: Array.from(report.bodyNames).slice(0, 8),
    })),
  });
  dispatchThrottledMeshCacheFailureToast("__missing_mesh_assets__");
}

function reportMissingMeshAsset(assetId, meshId, bodyName) {
  const key = assetId || "__unknown_mesh_asset__";
  const existing = missingMeshAssetReports.get(key) || {
    assetId: key,
    geomCount: 0,
    meshIds: new Set(),
    bodyNames: new Set(),
  };
  existing.geomCount += 1;
  if (meshId) existing.meshIds.add(meshId);
  if (bodyName) existing.bodyNames.add(bodyName);
  missingMeshAssetReports.set(key, existing);
  if (missingMeshAssetFlushQueued) return;
  missingMeshAssetFlushQueued = true;
  if (typeof globalThis.queueMicrotask === "function") {
    globalThis.queueMicrotask(flushMissingMeshAssetReports);
  } else {
    setTimeout(flushMissingMeshAssetReports, 0);
  }
}

function isAssetNotFoundError(error) {
  return String(error?.message || error || "").includes("[assetCache] Asset not found:");
}

const MATERIAL_TEXTURE_FIELDS = [
  ["colorMap", "map", "color"],
  ["alphaMap", "alphaMap", "alpha"],
  ["normalMap", "normalMap", "normal"],
  ["roughnessMap", "roughnessMap", "roughness"],
  ["metalnessMap", "metalnessMap", "metalness"],
  ["aoMap", "aoMap", "ao"],
  ["emissiveMap", "emissiveMap", "emissive"],
  ["bumpMap", "bumpMap", "bump"],
  ["displacementMap", "displacementMap", "displacement"],
  ["clearcoatMap", "clearcoatMap", "data"],
  ["clearcoatNormalMap", "clearcoatNormalMap", "normal"],
  ["clearcoatRoughnessMap", "clearcoatRoughnessMap", "roughness"],
  ["transmissionMap", "transmissionMap", "data"],
  ["thicknessMap", "thicknessMap", "data"],
];

function applyMaterialTextureSlots(material, materialDef, texturesState) {
  if (!material || !materialDef || !texturesState) return Promise.resolve();
  const work = MATERIAL_TEXTURE_FIELDS.map(([storeField, threeField, role]) => {
    const textureUuid = materialDef[storeField];
    const fingerprint = textureUuid ? texturesState[textureUuid]?.fingerprint || null : null;
    if (!fingerprint) return Promise.resolve();
    return acquireTextureFromCache(fingerprint)
      .then((tex) => {
        const clone = tex.clone();
        applyTextureRoleSettings(clone, role);
        if (storeField === "colorMap") {
          applyColorMapToTexture(clone, materialDef, texturesState[textureUuid] || null);
        }
        material[threeField] = clone;
        material.userData = material.userData || {};
        material.userData.assetTextureIds = {
          ...(material.userData.assetTextureIds || {}),
          [threeField]: fingerprint,
        };
        if (threeField === "map") {
          material.userData.assetTextureId = fingerprint;
        }
        applyMaterialTransparencySettings(material, materialDef);
        material.needsUpdate = true;
      })
      .catch((error) => {
        logger.warn("[processBodyFromStore] Failed to apply material texture", {
          material: materialDef.name,
          field: storeField,
          error,
        });
      });
  });
  return Promise.all(work);
}

// Bake non-uniform mesh scales into geometry to avoid visual skew when parents rotate
function bakeMeshScale(rootObj, scaleVec) {
  try {
    const sx = Array.isArray(scaleVec) && scaleVec.length === 3 ? Number(scaleVec[0]) : 1;
    const sy = Array.isArray(scaleVec) && scaleVec.length === 3 ? Number(scaleVec[1]) : 1;
    const sz = Array.isArray(scaleVec) && scaleVec.length === 3 ? Number(scaleVec[2]) : 1;
    const hasProvided = !(
      Math.abs(sx - 1) < 1e-6 &&
      Math.abs(sy - 1) < 1e-6 &&
      Math.abs(sz - 1) < 1e-6
    );
    const bakeIfNeeded = (mesh) => {
      const ms = new THREE.Vector3();
      mesh.getWorldScale(ms); // local scale check below prefers mesh.scale
      const lsx = hasProvided ? sx : mesh.scale.x;
      const lsy = hasProvided ? sy : mesh.scale.y;
      const lsz = hasProvided ? sz : mesh.scale.z;
      const nonUniform =
        Math.abs(lsx - 1) > 1e-6 || Math.abs(lsy - 1) > 1e-6 || Math.abs(lsz - 1) > 1e-6;
      if (!nonUniform) return;
      // clone geometry before mutation
      const geom = mesh.geometry && mesh.geometry.clone ? mesh.geometry.clone() : null;
      if (!geom) return;
      const m = new THREE.Matrix4().makeScale(lsx, lsy, lsz);
      geom.applyMatrix4(m);
      mesh.geometry = geom;
      mesh.userData = { ...mesh.userData, assetGeometryOwned: true };
      mesh.scale.set(1, 1, 1);
    };
    if (rootObj && typeof rootObj.traverse === "function") {
      rootObj.traverse((child) => {
        if (child && child.isMesh) bakeIfNeeded(child);
      });
    }
  } catch (e) {
    logger.warn("[processBodyFromStore] Failed to bake mesh scale:", e);
  }
}

export function attachMeshGeoms({
  bodyNode,
  visualGroup,
  materials,
  showCollision,
  showVisual,
  registerPendingMeshLoad,
}) {
  // Render mesh geoms directly from geoms list (ignore legacy bodyNode.meshes)
  const meshGeoms = (bodyNode.geoms || []).filter(
    (g) => g && typeof g.mesh === "string" && g.mesh.trim() !== ""
  );
  meshGeoms.forEach((geom) => {
    const meshId = geom.mesh; // now a Mesh UUID

    // Determine if this is a collision mesh based on visualOnly flag
    // visualOnly === true means visual-only (no collision)
    // visualOnly === false or undefined means it participates in collision
    const isCollisionMesh = geom.visualOnly !== true;

    let material;
    let resolvedMaterialUuid = null;
    if (isCollisionMesh) {
      // Use collision material for collision meshes
      material = createCollisionMaterial();
    } else {
      // Use visual material for visual meshes
      let materialId = geom?.materials?.[0] || null;

      const findDefaultMaterial = () => {
        return Object.values(materials).find((entry) => entry?.name === "default_mat") || null;
      };

      // Debug logging for material lookup
      if (materialId) {
        // Try to find material by name if UUID lookup fails
        if (!materials?.[materialId]) {
          const matByName = Object.values(materials).find((entry) => entry?.name === materialId);
          if (matByName) {
            devLog(
              `[processBodyFromStore] Material "${materialId}" not found by UUID, found by name: ${matByName.name} (UUID: ${matByName.uuid})`
            );
            materialId = matByName.uuid;
          } else {
            devLog(
              `[processBodyFromStore] Material "${materialId}" not found in materials map. Available materials:`,
              Object.keys(materials)
            );
            devLog(
              `[processBodyFromStore] Available material names:`,
              Object.values(materials).map((m) => m?.name)
            );
            devLog(`[processBodyFromStore] Geom materials array:`, geom?.materials);
          }
        } else {
          // Material found by UUID - log success
          const mat = materials[materialId];
          devLog(
            `[processBodyFromStore] Material "${materialId}" found: ${mat?.name || "unnamed"} (body: ${bodyNode.name}, geom: ${geom?.name || "unnamed"})`
          );
        }
      } else {
        devLog(
          `[processBodyFromStore] No material ID in geom (body: ${bodyNode.name}, geom: ${geom?.name || "unnamed"})`
        );
      }

      let mat = materialId ? materials?.[materialId] || null : null;
      if (!mat) {
        const fallback = findDefaultMaterial();
        if (fallback) {
          mat = fallback;
          materialId = fallback.uuid;
        }
      }

      if (mat) {
        const definition = { ...mat, name: mat.name || materialId || "default_mat" };
        material = buildMaterial(definition.name, definition, () => null);
        resolvedMaterialUuid = mat.uuid || materialId || null;

        try {
          const texturesState = getSceneState().textures;
          applyMaterialTextureSlots(material, mat, texturesState);
        } catch (error) {
          logger.warn("[processBodyFromStore] Unexpected error while processing body", error);
        }
      } else {
        const defaultMaterial = createDefaultPhongMaterial("default_mat");
        material = buildMaterial(defaultMaterial.name, defaultMaterial, () => null);
      }
    }

    // Load mesh
    const { meshes: meshDefinitions = {} } = getSceneState();
    let meshScale = [1, 1, 1];
    const meshDef = meshId ? meshDefinitions[meshId] : null;
    if (meshDef && Array.isArray(meshDef.scale) && meshDef.scale.length === 3) {
      meshScale = [...meshDef.scale];
    }
    const instanceId = `${bodyNode.name || "body"}:${(geom && geom.name) || meshId}`;

    // Look up asset ID via mesh definition fingerprint - required for all meshes
    const assetId = meshDef?.fingerprint;
    if (!assetId) {
      logger.error(
        `[processBodyFromStore] Mesh "${meshId}" has no fingerprint! Meshes must be processed through the asset store. Body: ${bodyNode.name}`
      );
      return; // Skip this mesh - it wasn't properly registered
    }
    if (!getAssetById(assetId)) {
      reportMissingMeshAsset(assetId, meshId, bodyNode.name);
      return;
    }

    const meshLoadPromise = acquireMeshFromCache(assetId)
      .then((res) => {
        let objToAdd = null;
        if (res && res.isBufferGeometry) {
          const mesh = new THREE.Mesh(res.clone(), material);
          mesh.userData.assetGeometryOwned = true;
          mesh.name = meshId;
          mesh.scale.set(...meshScale);
          objToAdd = mesh;
          bakeMeshScale(mesh, meshScale);
        } else if (res && typeof res.traverse === "function") {
          const cloned = res.clone(true);
          cloned.traverse((child) => {
            if (child.isMesh) {
              child.userData = { ...child.userData, assetGeometryOwned: false };
              child.material = material;
              child.name = meshId;
              child.scale.set(...meshScale);
            }
          });
          objToAdd = cloned;
          bakeMeshScale(cloned, meshScale);
        }
        if (!objToAdd) {
          logger.warn(`[processBodyFromStore] Failed to create mesh object for "${meshId}"`);
          return;
        }

        // Set userData on all mesh children
        objToAdd.traverse((child) => {
          if (child.isMesh) {
            if (isCollisionMesh) {
              child.visible = showCollision;
              child.userData = {
                ...child.userData,
                type: "collision",
                collisionGeomKind: "mesh",
                meshName: meshId,
                bodyName: bodyNode.name || meshId,
                bodyUuid: bodyNode.uuid,
                geomName: geom.name || `geom_${meshId}`,
                geomUuid: geom.uuid,
                selectable: true,
                assetId,
                instanceId,
                materialUuid: null,
              };
            } else {
              child.visible = showVisual;
              child.castShadow = true;
              child.receiveShadow = true;
              child.userData = {
                ...child.userData,
                type: "mesh",
                meshName: meshId,
                bodyName: bodyNode.name || meshId,
                bodyUuid: bodyNode.uuid,
                geomName: geom.name || `geom_${meshId}`,
                geomUuid: geom.uuid,
                selectable: true,
                assetId,
                instanceId,
                materialUuid: resolvedMaterialUuid,
              };
            }
          }
        });

        // Apply geom-local transform via nested groups.
        //
        // IMPORTANT:
        // In MJCF, mesh `refpos/refquat` belong to the *mesh asset's reference frame*.
        // The geom's `pos/quat` then places/orients that (already-referenced) mesh in the body frame.
        //
        // So the effective composition is:
        //   world = body * T_geom * R_geom * (inverse(refquat) * (meshVertices - refpos))
        //
        // This ordering matters when BOTH refquat and geom quat are non-identity (e.g. UR5e wrists).
        const geomPos = geom?.position || [0, 0, 0];
        const geomQuatArr = geom?.orientation || [1, 0, 0, 0];
        // Note: Don't use || for quaternion components - 0 is a valid value!
        const geomQ = new THREE.Quaternion(
          geomQuatArr[1] ?? 0, // x
          geomQuatArr[2] ?? 0, // y
          geomQuatArr[3] ?? 0, // z
          geomQuatArr[0] ?? 1 // w
        );

        // Mesh reference transform (MJCF refpos/refquat) converted to a vertex transform for Three.
        const meshDefs = getSceneState().meshes || {};
        const meshDef = meshDefs[meshId];
        const { position: refPosV, quat: refQ } = getVertexTransformFromMjcfMeshRef(meshDef);

        // Build transform hierarchy:
        //   outer (geom pos) -> geom (geom quat) -> ref (MJCF mesh ref) -> meshEditor -> mesh
        const outer = new THREE.Group();
        if (Array.isArray(geomPos) && geomPos.length === 3) {
          outer.position.set(geomPos[0], geomPos[1], geomPos[2]);
        }
        const geomGroup = new THREE.Group();
        geomGroup.quaternion.copy(geomQ);
        const refGroup = new THREE.Group();
        refGroup.position.copy(refPosV);
        refGroup.quaternion.copy(refQ);
        const meshEditorGroup = new THREE.Group();
        const meshEditorT = getPreviewTransformForMeshUuid(meshId);
        if (meshEditorT) {
          const toNum = (value, fallback) => {
            const n = Number(value);
            return Number.isFinite(n) ? n : fallback;
          };
          meshEditorGroup.position.set(
            toNum(meshEditorT.pos?.[0], 0),
            toNum(meshEditorT.pos?.[1], 0),
            toNum(meshEditorT.pos?.[2], 0)
          );
          const q = Array.isArray(meshEditorT.quat) ? meshEditorT.quat : [1, 0, 0, 0];
          meshEditorGroup.quaternion.set(
            toNum(q[1], 0),
            toNum(q[2], 0),
            toNum(q[3], 0),
            toNum(q[0], 1)
          );
        }
        objToAdd.position.set(0, 0, 0);
        objToAdd.quaternion.identity();

        // Tag transform layers for reliable UI updates
        outer.userData = {
          transformLayer: "outer",
          meshName: meshId,
          bodyName: bodyNode.name || meshId,
          bodyUuid: bodyNode.uuid,
          geomUuid: geom.uuid,
          instanceId,
        };
        geomGroup.userData = {
          transformLayer: "geom",
          meshName: meshId,
          bodyName: bodyNode.name || meshId,
          bodyUuid: bodyNode.uuid,
          geomUuid: geom.uuid,
          instanceId,
        };
        refGroup.userData = {
          transformLayer: "ref",
          meshName: meshId,
          bodyName: bodyNode.name || meshId,
          bodyUuid: bodyNode.uuid,
          geomUuid: geom.uuid,
          instanceId,
        };
        meshEditorGroup.userData = {
          transformLayer: "meshEditor",
          meshName: meshId,
          bodyName: bodyNode.name || meshId,
          bodyUuid: bodyNode.uuid,
          geomUuid: geom.uuid,
          instanceId,
        };

        meshEditorGroup.add(objToAdd);
        refGroup.add(meshEditorGroup);
        geomGroup.add(refGroup);
        outer.add(geomGroup);
        visualGroup.add(outer);
      })
      .catch((error) => {
        if (isAssetNotFoundError(error)) {
          reportMissingMeshAsset(assetId, meshId, bodyNode.name);
          return;
        }
        logger.error(
          `[processBodyFromStore] Failed to load mesh "${meshId}" from asset cache:`,
          error
        );
        dispatchThrottledMeshCacheFailureToast(assetId);
      });
    if (typeof registerPendingMeshLoad === "function") {
      registerPendingMeshLoad(meshLoadPromise, bodyNode.uuid);
    }
  });
}
